import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import _uniqBy from 'lodash/uniqBy';

import { queryStrings } from '@trqk/resolvers';
import {
    Dropdown,
    IconUpdate,
    LeftNavHeader,
    LoadingEllipses,
} from '@trqk/components';
import {
    locale,
    getAllListPages,
    getWebpackEnvVar,
    setInLocalStorage,
    removeNulls,
} from '@trqk/utils';
import { useGetTrqkEntityTracksLazyQuery } from '@trqk/api';

import { WebLayout } from '../../templates';
import { WorksTable } from './components';
import {
    useEntities,
    useToast,
    useTokenModal,
    useUploadStatements,
} from '../../../hooks';
import { LOCAL_STORAGE_KEYS } from '../../../constants';

const { GET_SESSION } = queryStrings;

const SCRIPT_BLOCKER_CALL_SITE_ID = 'Works';

const workTypeOptions = [
    {
        label: locale.enUS.WORKS.WORK_TYPE_FILTER.LABELS.ALL,
        value: 'ALL' as WorkType,
    },
    {
        label: locale.enUS.WORKS.WORK_TYPE_FILTER.LABELS.TRACKS,
        value: 'TRACK' as WorkType,
    },
    {
        label: locale.enUS.WORKS.WORK_TYPE_FILTER.LABELS.CUE_SHEETS,
        value: 'CUE_SHEET' as WorkType,
    },
];

const defaultWorkTypeIndex = 0;

const defaultWorkType = workTypeOptions[defaultWorkTypeIndex].value;

interface UpdateWorkListsInput {
    tracks: (TrqkTrackTableRow | null)[];
    replace: boolean;
    deduplicate: boolean;
}

/**
 * Displays a table of the user's statement works, allowing them to search and sort, as well as
 * click a work title to see details for the work. The list of work is determined by the currently
 * selected entity.
 *
 * The list of works can get out of date when a statement is added or deleted. It takes some time
 * for the backend to rebuild the track catalog and update the entity with the updated track list.
 * There are several mechanisms for dealing with this:
 * - a URL parameter can be used to trigger a refresh of the current list
 * - the UploadStatementsProvider keeps track of the entityRefreshState for each entity, which
 *   includes a timestamp of when the works were last fetched for the entity as well
 *   as when the entityRefreshed.success event was logged. The entityRefreshed.success event tells
 *   us when the tracks list has been updated. If the works have not been fetched since
 *   the last time entityRefresh.success was logged, we re-fetch them.
 * - the user can click the "Refresh" button to force a re-fetch of the works as well
 */
export const Works = (): JSX.Element => {
    const { noSelectedEntity, selectedEntityId } = useEntities();
    const sessionQuery = useQuery(GET_SESSION);
    const { getToken } = useTokenModal();
    const { add: addToast } = useToast();
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const userId = sessionQuery?.data?.session?.currentUser?.id ?? null;

    // A URL with a refresh parameter would look like: '/works/refresh/1606939789922'
    const urlRefreshTime = parseInt(
        useParams<{ refreshTime?: string }>().refreshTime ?? '0',
        10,
    );

    // If refreshTime is changed, the table data is set to the incoming data. This happens either
    // when a URL `refresh` parameter is updated, or the getTrqkEntity call finishes.
    const [refreshTime, setRefreshTime] = useState(0);

    // Track refreshes triggered by ingestion / deletion
    const { entityRefreshState, setEntityRefreshState } = useUploadStatements();
    const entityRefreshedAt =
        selectedEntityId === null
            ? 0
            : entityRefreshState?.[selectedEntityId]?.refreshedAt ?? 0;
    const entityTracksFetchedAt =
        selectedEntityId === null
            ? 0
            : entityRefreshState?.[selectedEntityId]?.tracksFetchedAt ?? 0;

    const [workLists, setWorkLists] = useState<
        Record<WorkType, WorksTableRow[]>
    >({
        ALL: [],
        CUE_SHEET: [],
        TRACK: [],
    });

    /**
     * Update the state of the `workLists`
     * @param tracks - array of {@link TrqkTrackTableRow} objects. Because of a loosely typed schema
     * this array can also contain `null` values.
     * @param replace - whether to overwrite the current `workLists`. This is appropriate when
     * loading the initial list of tracks for a given entity. When the user scrolls to the bottom
     * of the current list, this should be set to `false` to concatenate the each of the next
     * `workLists` arrays to the current one.
     */
    const updateWorkLists = ({
        tracks,
        replace,
        deduplicate,
    }: UpdateWorkListsInput): void => {
        if (tracks === null) {
            setWorkLists({
                ALL: [],
                CUE_SHEET: [],
                TRACK: [],
            });
        } else {
            setWorkLists(
                (
                    prev: Record<WorkType, WorksTableRow[]>,
                ): Record<WorkType, WorksTableRow[]> => {
                    const nextAllWorks: WorksTableRow[] = replace
                        ? []
                        : [...prev.ALL];
                    const nextTracks: WorksTableRow[] = replace
                        ? []
                        : [...prev.TRACK];
                    const nextCueSheets: WorksTableRow[] = replace
                        ? []
                        : [...prev.CUE_SHEET];

                    tracks.forEach((track: null | TrqkTrackTableRow): void => {
                        if (track !== null) {
                            const { id, title } = track;

                            const row = {
                                id,
                                asset_url: track?.asset_url ?? null,
                                audio_id: track?.audio_id ?? null,
                                detection_status:
                                    track?.detection_status ?? null,
                                earnings: track?.earnings ?? null,
                                filename: track?.filename ?? null,
                                percent_share: track?.percent_share ?? null,
                                performance_count:
                                    track?.performance_count ?? null,
                                song_id: id,
                                title: track?.title ?? null,
                                work_ids: removeNulls<string>(track.work_ids),
                                uploading_filename: null,
                                progress: null,
                            };

                            nextAllWorks.push(row);

                            if (title?.toLowerCase().includes('cues')) {
                                nextCueSheets.push(row);
                            } else {
                                nextTracks.push(row);
                            }
                        }
                    });

                    return {
                        ALL: deduplicate
                            ? _uniqBy(nextAllWorks, 'id')
                            : nextAllWorks,
                        CUE_SHEET: deduplicate
                            ? _uniqBy(nextAllWorks, 'id')
                            : nextCueSheets,
                        TRACK: deduplicate
                            ? _uniqBy(nextTracks, 'id')
                            : nextTracks,
                    };
                },
            );
        }

        // Update local refreshTime state
        setRefreshTime(Date.now());
    };

    // Keep track of the song_ids that have been used to fetch more tracks
    const usedSongIdsRef = useRef<string[]>([]);

    // `lastSongId` represents the id for the last work in the current list of all works. It will be
    // used as a kind of pagination token when requesting the next page of results within
    // `fetchMore` below.
    const lastSongId = workLists.ALL?.[workLists.ALL.length - 1]?.id ?? null;

    /**
     * When the user scrolls to the bottom of the tracks list, fetch the next page of tracks and use
     * it to update the `worksLists`.
     */
    const fetchMore = useCallback((): void => {
        const usedSongIds = usedSongIdsRef?.current ?? null;

        if (usedSongIds === null) {
            return undefined;
        }

        if (lastSongId === null || usedSongIds.includes(lastSongId)) {
            // Do not fetch the next page if this song_id has already been used to fetch more tracks
            return undefined;
        }

        usedSongIdsRef.current.push(lastSongId);

        // Before fetching more table rows, set a flag for this call site in localStorage for
        // CheckForScriptBlocker. See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
        setInLocalStorage<Record<string, number>>(
            LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
            (prev: Record<string, number> | null): Record<string, number> => {
                const next =
                    prev === null
                        ? {}
                        : {
                              ...prev,
                              [SCRIPT_BLOCKER_CALL_SITE_ID]: Date.now(),
                          };

                return next;
            },
        );

        getAllListPages<TrqkTrackTableRow, TableTrqkTableRowFilterInput>({
            getToken,
            query: `
                query listTrqkTrackTableRows(
                    $filter: TableTrqkTableRowFilterInput
                    $nextToken: String
                    $limit: Int
                ) {
                    listTrqkTrackTableRows(filter: $filter, nextToken: $nextToken, limit: $limit) {
                        items {
                            id
                            asset_url
                            audio_id
                            detection_status
                            earnings
                            filename
                            percent_share
                            performance_count
                            title
                            work_ids
                        }
                        nextToken
                    }
                }
            `,
            filter: {
                entity_id: {
                    eq: selectedEntityId,
                },
                song_id: {
                    gt: lastSongId,
                },
            },
            limit: 1000,
            queryName: 'listTrqkTrackTableRows',
            trqkDirectoryUrl,
        })
            .then((nextTracksPage: TrqkTrackTableRow[]): void => {
                updateWorkLists({
                    tracks: nextTracksPage,
                    replace: false,
                    deduplicate: false,
                });

                // After successfully fetching log events, delete the flag for this call site in
                // localStorage for CheckForScriptBlocker. See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                setInLocalStorage<Record<string, number>>(
                    LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
                    (
                        prev: Record<string, number> | null,
                    ): Record<string, number> => {
                        const next = prev === null ? {} : { ...prev };

                        delete next[SCRIPT_BLOCKER_CALL_SITE_ID];

                        return next;
                    },
                );
            })
            .catch((): void => {
                // After an error when fetching the works:

                // 1. Show an error toast
                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.WORKS.ERRORS.LIST_TRQK_TABLE_ROWS,
                });

                // 2. Delete the flag for this call site in localStorage for CheckForScriptBlocker.
                // See: packages/web/src/components/modals/CheckForScriptBlocker/CheckForScriptBlocker.tsx
                setInLocalStorage<Record<string, number>>(
                    LOCAL_STORAGE_KEYS.SCRIPT_BLOCKER_FLAGS,
                    (
                        prev: Record<string, number> | null,
                    ): Record<string, number> => {
                        const next = prev === null ? {} : { ...prev };

                        delete next[SCRIPT_BLOCKER_CALL_SITE_ID];

                        return next;
                    },
                );
            });

        return undefined;
    }, [
        addToast,
        getToken,
        lastSongId,
        selectedEntityId,
        usedSongIdsRef.current,
    ]);

    /**
     * When the user enters a value in the search field, fetch filtered tracks and add them to the
     * `worksLists`
     */
    const searchForMore = useCallback(
        (searchTerm?: string): void => {
            if (searchTerm && searchTerm.length > 0) {
                getAllListPages<
                    TrqkTrackTableRow,
                    TableTrqkTableRowFilterInput
                >({
                    getToken,
                    query: `
                            query listTrqkTrackTableRows(
                                $filter: TableTrqkTableRowFilterInput
                                $nextToken: String
                                $limit: Int
                            ) {
                                listTrqkTrackTableRows(filter: $filter, nextToken: $nextToken, limit: $limit) {
                                    items {
                                        id
                                        asset_url
                                        audio_id
                                        detection_status
                                        earnings
                                        filename¡
                                        percent_share
                                        performance_count
                                        title
                                        work_ids
                                    }
                                    nextToken
                                }
                            }
                        `,
                    filter: {
                        entity_id: {
                            eq: selectedEntityId,
                        },
                        title: {
                            contains: searchTerm,
                        },
                    },
                    limit: 1000,
                    queryName: 'listTrqkTrackTableRows',
                    trqkDirectoryUrl,
                })
                    .then((searchResults: TrqkTrackTableRow[]): void => {
                        updateWorkLists({
                            tracks: searchResults,
                            replace: false,
                            deduplicate: true,
                        });
                    })
                    .catch((): void => {
                        addToast({
                            kind: 'warning-banner',
                            content:
                                locale.enUS.WORKS.ERRORS.LIST_TRQK_TABLE_ROWS,
                        });
                    });
            }
        },
        [addToast, getToken, selectedEntityId],
    );

    const [getTrqkEntity, getTrqkEntityQuery] = useGetTrqkEntityTracksLazyQuery(
        {
            fetchPolicy: 'network-only',
            notifyOnNetworkStatusChange: true,
            onCompleted: data => {
                const tracks = data?.getTrqkEntity?.tracks ?? null;

                if (tracks === null) {
                    addToast({
                        kind: 'warning-banner',
                        content: locale.enUS.WORKS.ERRORS.GET_TRQK_ENTITY,
                    });
                } else {
                    updateWorkLists({
                        tracks,
                        replace: true,
                        deduplicate: false,
                    });

                    // Update entityRefreshState in UploadStatementsProvider for the current entity
                    setEntityRefreshState(
                        (prev: EntityRefreshState): EntityRefreshState => {
                            const prevEntityStatus =
                                selectedEntityId === null
                                    ? null
                                    : prev?.[selectedEntityId] ?? null;

                            if (
                                prevEntityStatus !== null &&
                                selectedEntityId !== null
                            ) {
                                const nextEntityStatus = {
                                    ...prevEntityStatus,
                                    tracksFetchedAt: Date.now(),
                                };

                                return {
                                    ...prev,
                                    [selectedEntityId]: nextEntityStatus,
                                };
                            }

                            return prev;
                        },
                    );
                }
            },
            onError: () => {
                addToast({
                    kind: 'warning-banner',
                    content: locale.enUS.WORKS.ERRORS.GET_TRQK_ENTITY,
                });
            },
        },
    );

    const fetchStatementTracks = useCallback((): void => {
        if (selectedEntityId !== null && userId !== null) {
            getTrqkEntity({
                variables: {
                    id: selectedEntityId,
                    user_id: userId,
                },
            });
        }
    }, [selectedEntityId, userId, getTrqkEntity]);

    useEffect(() => {
        if (selectedEntityId !== null && userId !== null) {
            fetchStatementTracks();
        }
    }, [selectedEntityId, fetchStatementTracks, userId]);

    // If the works page has a refreshTime in its URL that is more recent than the last refresh
    // timestamp, re-fetch the track data
    useEffect(() => {
        if (userId && urlRefreshTime && urlRefreshTime > refreshTime) {
            fetchStatementTracks();
        }
    }, [fetchStatementTracks, urlRefreshTime, refreshTime, userId]);

    // Listen for changes in this entity's "refresh state", which can be triggered by statement
    // ingestion / deletion. On a new `entityRefreshedAt`, re-fetch the track data
    useEffect(() => {
        if (
            entityTracksFetchedAt < entityRefreshedAt &&
            entityRefreshedAt > 0 &&
            getTrqkEntityQuery?.refetch !== undefined
        ) {
            getTrqkEntityQuery.refetch();
        }
    }, [entityTracksFetchedAt, entityRefreshedAt, getTrqkEntityQuery.refetch]);

    const name = getTrqkEntityQuery?.data?.getTrqkEntity?.name ?? null;
    const roleType = getTrqkEntityQuery?.data?.getTrqkEntity?.role_type ?? null;

    const getTrqkEntityQueryIsLoading =
        !noSelectedEntity &&
        (!getTrqkEntityQuery.called || getTrqkEntityQuery.loading);
    const tableDataLoading = getTrqkEntityQueryIsLoading;

    // Track the state of manual refreshes caused by user clicking the refresh button
    const [isRefreshing, setIsRefreshing] = useState(false);

    useEffect(() => {
        if (isRefreshing) {
            const timeoutId = setTimeout((): void => {
                setIsRefreshing(false);
            }, 2000);

            return (): void => {
                clearTimeout(timeoutId);
            };
        }

        return (): void => undefined;
    }, [isRefreshing]);

    const [workType, setWorkType] = useState<WorkType>(defaultWorkType);
    const showRefreshButton = !tableDataLoading && !isRefreshing;
    const showRefreshLoader = tableDataLoading || isRefreshing;

    return (
        <WebLayout
            mainWidth={null}
            headerPadding="xs"
            loading={tableDataLoading}
            header={{
                content: (
                    <LeftNavHeader>
                        <div>
                            <div className="flex justify-between items-start">
                                <h3 data-cy="works-title">
                                    {locale.enUS.WORKS.TITLE}
                                </h3>
                            </div>
                            <div className="mt-3 text-sm">
                                {locale.enUS.WORKS.DESCRIPTION.PART_1}
                                <br />
                                {locale.enUS.WORKS.DESCRIPTION.PART_2}
                            </div>
                        </div>
                    </LeftNavHeader>
                ),
            }}
        >
            <div className="flex flex-col bg-white rounded-lg rounded-tr-lg pb-6 px-6 pt-4">
                <WorksTable
                    header={
                        <div className="flex justify-end items-center space-x-2 w-full">
                            <Dropdown<WorkType>
                                borderConfig="solid.small"
                                dataCy="work-type-dropdown"
                                defaultIndex={defaultWorkTypeIndex}
                                description={
                                    locale.enUS.WORKS.WORK_TYPE_FILTER
                                        .DESCRIPTION
                                }
                                handleChange={(value: WorkType): void => {
                                    setWorkType(value);
                                }}
                                options={workTypeOptions}
                                width={220}
                                height={42}
                            />
                            <div
                                style={{ height: 41, width: 41 }}
                                className="flex items-center justify-center"
                            >
                                {showRefreshLoader && (
                                    <LoadingEllipses
                                        width={30}
                                        fillColor="secondary-400"
                                    />
                                )}
                                {showRefreshButton && (
                                    <button
                                        type="button"
                                        className="text-xs text-secondary-500 italic underline border border-secondary-500 rounded p-2"
                                        data-cy="refresh-works-button"
                                        onClick={(): void => {
                                            fetchStatementTracks();
                                            setIsRefreshing(true);
                                        }}
                                    >
                                        <IconUpdate fillColor="secondary-500" />
                                    </button>
                                )}
                            </div>
                        </div>
                    }
                    name={name}
                    onScrollToBottom={fetchMore}
                    onSearch={searchForMore}
                    refetch={fetchStatementTracks}
                    refreshTime={refreshTime}
                    roleType={roleType}
                    tracks={workLists[workType]}
                    userId={userId}
                />
            </div>
        </WebLayout>
    );
};
